import React, { createContext, useContext, useState, ReactNode } from "react";

export type SnackbarSeverity = "success" | "info" | "error";

export interface SnackbarOptions {
  message: string;
  severity?: SnackbarSeverity;
  actionLabel?: string;
  onAction?: () => void;
  duration?: number;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: SnackbarSeverity;
  actionLabel?: string;
  onAction?: () => void;
}

interface SnackbarContextType {
  showSnackbar: (options: SnackbarOptions) => void;
  hideSnackbar: () => void;
  snackbarState: SnackbarState;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

interface SnackbarProviderProps {
  children: ReactNode;
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
  const [snackbarState, setSnackbarState] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "info",
  });

  const showSnackbar = (options: SnackbarOptions) => {
    setSnackbarState({
      open: true,
      message: options.message,
      severity: options.severity || "info",
      actionLabel: options.actionLabel,
      onAction: options.onAction,
    });

    // Auto-hide after duration (default 6 seconds)
    const duration = options.duration || 6000;
    setTimeout(() => {
      hideSnackbar();
    }, duration);
  };

  const hideSnackbar = () => {
    setSnackbarState(prev => ({ ...prev, open: false }));
  };

  const value = {
    showSnackbar,
    hideSnackbar,
    snackbarState,
  };

  return (
    <SnackbarContext.Provider value={value}>
      {children}
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (context === undefined) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
};
